import { isObject } from 'underscore';
import { TReactStylesMethod } from './custom-react-select.types';
import { Property } from 'csstype';

export const reactSelectAdminStyles: TReactStylesMethod = (styles) => ({
  container: (base, props) => ({
    ...base,
    width: '100%',
    ...styles?.container?.(base, props),
  }),
  clearIndicator: (base, props) => ({
    ...base,
    ...styles?.clearIndicator?.(base, props),
  }),
  control: (base, props) => {
    return {
      ...base,
      minHeight: '32px',
      maxHeight: 'fit-content',
      display: 'flex',
      alignContent: 'center',
      padding: '0 6px 0 6px',
      border: '1px solid var(--gray-400)',
      borderRadius: '25px',
      backgroundColor: 'transparent',
      transition: 'border-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
      boxShadow: 'none',
      borderWidth: '1px',
      ...(props.isFocused || props.menuIsOpen
        ? {
            borderColor: 'var(--gray-100) !important',
          }
        : {}),
      '&:hover': {
        ...(isObject(base?.['&:hover'])
          ? {
              ...(base?.['&:hover'] as Record<string, string>),
              borderColor: 'var(--gray-100)',
            }
          : {}),
      },
      ...styles?.control?.(base, props),
    };
  },
  dropdownIndicator: (base, props) => ({
    ...base,
    color: 'var(--gray-100)',
    '&:hover': {
      color: 'var(--gray-200)',
    },
    transition: 'transform 150ms ease-in-out',
    ...(props?.selectProps?.menuIsOpen
      ? {
          transform: 'rotate(180deg)',
        }
      : {}),
    svg: {
      pointerEvents: 'none',
    },
    ...styles?.dropdownIndicator?.(base, props),
  }),
  group: (base, props) => ({
    ...base,
    ...styles?.group?.(base, props),
  }),
  groupHeading: (base, props) => ({
    ...base,
    ...styles?.groupHeading?.(base, props),
  }),
  placeholder: (base, props) => ({
    ...base,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '0.02em',
    color: 'var(--gray-400)',
    ...styles?.placeholder?.(base, props),
  }),
  loadingIndicator: (base, props) => ({
    ...base,
    ...styles?.loadingIndicator?.(base, props),
  }),
  loadingMessage: (base, props) => ({
    ...base,
    ...styles?.loadingMessage?.(base, props),
  }),
  menu: (base, props) => ({
    ...base,
    backgroundColor: 'var(--gray-800)',
    borderRadius: '12px',
    zIndex: 5,
    ...styles?.menu?.(base, props),
  }),
  indicatorSeparator: (base, props) => ({
    ...base,
    display: 'none',
    ...styles?.indicatorSeparator?.(base, props),
  }),
  menuList: (base, props) => ({
    ...base,
    backgroundColor: 'var(--gray-800)',
    borderRadius: '12px',
    padding: 0,
    fontSize: '14px',
    ...styles?.menuList?.(base, props),
  }),
  menuPortal: (base, props) => ({
    ...base,
    ...styles?.menuPortal?.(base, props),
  }),
  option: (base, props) => {
    const baseAndAdminStyles = {
      ...base,
      cursor: 'pointer',
      wordBreak: 'break-word' as Property.WordBreak,
      transition: 'background-color 150ms ease-in-out',
      '&:active': {
        ...(props.isSelected ? { backgroundColor: 'var(--gray-700)' } : { backgroundColor: 'var(--gray-700)' }),
      },
      '&:hover': {
        ...(!props.isFocused ? { backgroundColor: 'var(--gray-700)' } : {}),
      },
      ...(props.isFocused
        ? {
            backgroundColor: 'var(--gray-700)',
          }
        : {}),
      ...(props.isSelected
        ? {
            backgroundColor: 'var(--gray-700)',
          }
        : {}),
    };
    return styles?.option ? styles.option(baseAndAdminStyles, props) : baseAndAdminStyles;
  },
  indicatorsContainer: (base, props) => ({
    ...base,
    ...styles?.indicatorsContainer?.(base, props),
  }),
  input: (base, props) => ({
    ...base,
    color: 'var(--gray-200)',
    maxWidth: '100%',
    overflow: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    ...styles?.input?.(base, props),
  }),
  singleValue: (base, props) => ({
    ...base,
    color: 'var(--gray-200)',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '0.02em',
    ...styles?.singleValue?.(base, props),
  }),
  multiValue: (base, props) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    border: '1px solid var(--gray-400)',
    backgroundColor: 'var(--gray-900)',
    borderRadius: '25px',
    paddingLeft: '8px',
    ...styles?.multiValue?.(base, props),
  }),
  multiValueLabel: (base, props) => ({
    ...base,
    fontSize: '12px',
    textTransform: 'uppercase',
    paddingLeft: '0',
    color: 'var(--gray-200)',
    ...styles?.multiValueLabel?.(base, props),
  }),
  multiValueRemove: (base, props) => ({
    ...base,
    marginRight: '4px',
    cursor: 'pointer',
    transition: 'background-color 150ms ease-in-out',
    '&:hover': {
      backgroundColor: 'var(--gray-600)',
      color: 'var(--gray-200)',
    },
    ...(props.isFocused
      ? {
          backgroundColor: 'var(--gray-600)',
          color: 'var(--gray-200)',
        }
      : {}),
    ...styles?.multiValueRemove?.(base, props),
  }),
  noOptionsMessage: (base, props) => ({
    ...base,
    ...styles?.noOptionsMessage?.(base, props),
  }),
  valueContainer: (base, props) => ({
    ...base,
    ...styles?.valueContainer?.(base, props),
  }),
});
